import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import styles from "./Resumen.module.scss";
import { Mail, Phone, Globe, Instagram, Linkedin, Facebook, Twitter, CircleCheck, MapPin, Sparkles } from "lucide-react";
import MisSkills from "../MisSkills/MisSkills.js";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader.js";
import { useActionGuard } from "../../../../hooks/useActionGuard.ts";
import { normalizeUserCommerces } from "../../../../utils/userCommerces.ts";
import { buildProfileUrl, getUserDisplayName } from "../../../../utils/navigationHelpers.ts";

const MIN_LOADING_TIME = 400;
const MAX_LOADING_TIME = 8000;

interface CategoryConnectItem {
  name?: string;
}

interface JobBoardZone {
  allCountry?: boolean;
  jurisdiccion?: { name?: string };
  city?: string;
}

interface ProfileData {
  id?: string | number;
  username?: string;
  description?: string | null;
  type?: string;
  linkedin?: string;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  phone?: string;
  jobBoardZones?: JobBoardZone[];
  categoryConnect?: CategoryConnectItem[];
  commerces?: { id?: number; name?: string }[];
}

interface ResumenProps {
  myPerson?: boolean;
  userData?: ProfileData | null;
  profileVisualizerData?: ProfileData | null;
  perfilAjeno?: string | number;
  setInfoReady: (ready: boolean) => void;
  onOpenPostModal?: () => void;
}

const Resumen: React.FC<ResumenProps> = ({ myPerson, userData, profileVisualizerData, perfilAjeno, setInfoReady, onOpenPostModal }) => {
  const { guard } = useActionGuard();
  const [isDataReady, setIsDataReady] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setIsDataReady(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const dataSource = (myPerson ? userData : profileVisualizerData) ?? {};
    const isCorrectUser = myPerson ? true : (profileVisualizerData?.id === perfilAjeno || !perfilAjeno);
    const hasData = dataSource && dataSource.username && isCorrectUser;

    if (hasData) {
      timeoutRef.current = setTimeout(() => {
        setIsDataReady(true);
        setInfoReady(true);
      }, MIN_LOADING_TIME);
    } else {
      timeoutRef.current = setTimeout(() => {
        setIsDataReady(true);
        setInfoReady(true);
      }, MAX_LOADING_TIME);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [myPerson, userData, profileVisualizerData, perfilAjeno, setInfoReady]);

  const displayData = (myPerson ? userData : profileVisualizerData) ?? {};
  const {
    linkedin,
    instagram,
    facebook,
    twitter,
    phone,
    type,
    description,
    jobBoardZones,
    username,
    categoryConnect,
  } = displayData;

  const ownerCommerces = myPerson ? normalizeUserCommerces(userData ?? {}) : [];
  const gestionMiComercioHref =
    myPerson && userData?.id != null
      ? buildProfileUrl(String(userData.id), getUserDisplayName(userData), "Gestión", {
          section: "comercio",
        })
      : "";

  const visitorCommerceList = !myPerson ? normalizeUserCommerces(displayData) : [];

  const formatCategoryConnectText = (): string | null => {
    const categories = Array.isArray(categoryConnect) ? categoryConnect.map((c) => c?.name).filter(Boolean) : [];
    if (categories.length === 0) return null;
    const list = categories.length === 1
      ? categories[0]
      : categories.length === 2
        ? `${categories[0]} y ${categories[1]}`
        : `${categories.slice(0, -1).join(", ")} y ${categories[categories.length - 1]}`;
    const listPhrase = categories.length > 1 ? `áreas como ${list}` : list;
    if (myPerson) {
      return `Te interesa conectar con profesionales de ${listPhrase}.`;
    }
    if (type === "COMPANY") {
      return `La empresa está interesada en conectar con profesionales de ${listPhrase}.`;
    }
    return `Le interesa conectar con profesionales de ${listPhrase}.`;
  };

  const categoryConnectText = formatCategoryConnectText();

  return (
    <>
      {!isDataReady ? (
        <div className={styles.loadingContainer}>
          <RegisteredUserProfileLoader smallVersion={false} noBackground={true} />
        </div>
      ) : (
        <div className={styles.resumen}>

          <div className={styles.resumen_leftSide}>
            <div className={styles.resumen_section}>
              <h4 className={styles.resumen_section_title}>
                Contacto
              </h4>
              <div className={styles.resumen_section_info}>
                <div className={styles.resumen_section_info_item}>
                  <Mail size={20} className={styles.resumen_section_info_item_icon} />
                  <p className={styles.resumen_section_info_item_data}>{username ?? ""}</p>
                </div>
                {phone && (
                  <div className={styles.resumen_section_info_item}>
                    <Phone size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{phone}</p>
                  </div>
                )}
                {instagram && (
                  <div className={styles.resumen_section_info_item}>
                    <Instagram size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{instagram}</p>
                  </div>
                )}
                {linkedin && (
                  <div className={styles.resumen_section_info_item}>
                    <Linkedin size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{linkedin}</p>
                  </div>
                )}
                {facebook && (
                  <div className={styles.resumen_section_info_item}>
                    <Facebook size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{facebook}</p>
                  </div>
                )}
                {twitter && (
                  <div className={styles.resumen_section_info_item}>
                    <Twitter size={20} className={styles.resumen_section_info_item_icon} />
                    <p className={styles.resumen_section_info_item_data}>{twitter}</p>
                  </div>
                )}
              </div>
            </div>
            {categoryConnectText && (
              <div className={styles.resumen_section}>
                <h4 className={styles.resumen_section_title}>
                  Conexiones
                </h4>
                <p className={styles.resumen_section_text}>{categoryConnectText}</p>
              </div>
            )}
            {myPerson && (
              <div className={styles.resumen_section}>
                <h4 className={styles.resumen_section_title}>Materiales en Edify</h4>
                {ownerCommerces.length > 0 ? (
                  <p className={styles.commerceSummaryName}>
                    {ownerCommerces.length === 1
                      ? ownerCommerces[0]?.name?.trim() || "Comercio registrado"
                      : ownerCommerces.map((c) => c.name).join(" · ")}
                  </p>
                ) : null}
                <p className={styles.resumen_section_text}>
                  {ownerCommerces.length === 0
                    ? "Sin comercio dado de alta."
                    : ownerCommerces.length === 1
                      ? ownerCommerces[0]?.name?.trim()
                        ? "Tu listado de materiales se muestra en este perfil público."
                        : "Comercio activo; el nombre y los datos los completás en Administrar."
                      : "Cada local tiene su catálogo visible desde este perfil."}
                </p>
                {gestionMiComercioHref ? (
                  <Link className={styles.commerceAltaLink} to={gestionMiComercioHref}>
                    Administrar
                  </Link>
                ) : null}
              </div>
            )}
          </div>
          <div className={styles.resumen_rightSide}>
            <div className={styles.resumen_section}>
              <div className={styles.resumen_section_header}>
                <h4 className={styles.resumen_section_title}>
                  Descripción {type === "COMPANY" ? "de la empresa" : "profesional"}
                </h4>
                {myPerson && onOpenPostModal && (
                  <button
                    type="button"
                    className={styles.generatePostButton}
                    onClick={guard("generate_post", onOpenPostModal)}
                    title="Generar texto para presentarte"
                  >
                    <Sparkles size={16} />
                    Generar post
                  </button>
                )}
              </div>
              <p className={styles.resumen_section_text}>
                {description === null || description === "" ? "No especificado" : (description ?? "No especificado")}
              </p>
            </div>
            <div className={styles.resumen_section}>
              <h4 className={styles.resumen_section_title}>
                Rubros
              </h4>
              <MisSkills perfilAjeno={perfilAjeno} />
            </div>
            {!myPerson &&
            visitorCommerceList.length > 0 &&
            (displayData.id != null || perfilAjeno != null) ? (
              <div className={styles.resumen_section}>
                <h4 className={styles.resumen_section_title}>Materiales</h4>
                <p className={styles.resumen_section_text}>
                  {visitorCommerceList.length === 1
                    ? visitorCommerceList[0]?.name
                      ? `Este perfil publica listado de materiales (${visitorCommerceList[0].name}).`
                      : "Este perfil publica listado de materiales en Edify."
                    : `Este perfil publica materiales desde ${visitorCommerceList.length} comercios en Edify.`}
                </p>
                <Link
                  className={styles.commerceAltaLink}
                  to={buildProfileUrl(
                    String(displayData.id ?? perfilAjeno),
                    getUserDisplayName(displayData),
                    "Materiales"
                  )}
                >
                  Ver listado
                </Link>
              </div>
            ) : null}
            <div className={styles.resumen_section}>
              <h4 className={styles.resumen_section_title}>
                Zona de trabajo
              </h4>
              <div className={styles.resumen_section_info}>
                {jobBoardZones && jobBoardZones.some(zone => zone?.allCountry) ? (
                  <div className={styles.resumen_section_info_allCountry}>
                    <Globe size={20} className={styles.resumen_section_info_item_icon} />
                    <div className={styles.resumen_section_info_allCountry_data}>
                      <p className={styles.resumen_section_info_allCountry_data_title}>Disponibilidad nacional</p>
                      <p className={styles.resumen_section_info_allCountry_data_text}>Servicios/productos disponibles en todo el país</p>
                    </div>
                    <CircleCheck size={20} className={styles.resumen_section_info_allCountry_icon} />
                  </div>
                ) : (
                  <div className={styles.resumen_section_info_zones}>
                    {jobBoardZones && jobBoardZones.map((zone, index) => (
                      <div key={index} className={styles.resumen_section_info_zones_item}>
                        <MapPin size={20} className={styles.resumen_section_info_item_icon} />
                        <div className={styles.resumen_section_info_item_data}>
                          <p className={styles.resumen_section_info_allCountry_data_title}>{zone?.jurisdiccion?.name}</p>
                          <p className={styles.resumen_section_info_item_allCountry_text}>{zone?.city?.split(',')[0]}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

};

export default Resumen;
